import trackEvent from '@ahd/shared/tools/track-event';
import { TrackEventOrigin } from '@ahd/shared/types/tracking/matomo/event-origin';
import { TrackEventSubType } from '@ahd/shared/types/tracking/matomo/event-sub-type';
import { type TFunction } from 'i18next';
import { type Control, type DefaultValues, type UseFormHandleSubmit, type UseFormReset, type UseFormReturn, type UseFormSetError, type UseFormSetValue, useForm } from 'react-hook-form';
import { DebugLevel, type ConfigObject } from '../../_configs/types';
import handleCarList from '../../_tools/axios/cars/helpers/handleCarList';
import handleFacets from '../../_tools/axios/cars/helpers/handleFacets';
import type { FaarenListCar } from '../../_tools/types/faaren';
import { type Car, type CarList, type Facets } from '../../_types/Car';
import { type Modules } from '../../modules/_definition/types';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import { type UseCarSearchReturn } from './types';

export type RegisterFormReturn<T> = {
    control: Control<T, any>;
    handleSubmit: UseFormHandleSubmit<T, undefined>;
    methods: UseFormReturn<T, any, undefined>;
    reset: UseFormReset<T>;
    setError: UseFormSetError<T>;
    setValue: UseFormSetValue<T>;
};

export const registerForm = <T>(defaultValues: DefaultValues<T>): RegisterFormReturn<T> => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const methods = useForm({
        defaultValues,
    });

    const { control, handleSubmit, reset, setError, setValue } = methods;

    return {
        control,
        handleSubmit,
        methods,
        reset,
        setError,
        setValue,
    };
};

type HandleListDataProps = {
    allFaarenVehicles: FaarenListCar[];
    listServiceData: CarList;
    module: Modules;
    searchForOcto: { keyword: string; category: string }[];
    settings: ConfigObject;
    translate: TFunction;
};

export async function handleListData(props: HandleListDataProps): Promise<UseCarSearchReturn['results'] & { error: unknown }> {
    try {
        const { allFaarenVehicles, listServiceData, module, searchForOcto, settings, translate } = props;
        const cars: Record<string, Car<false>> = await handleCarList(listServiceData?.searchResult || {}, module, settings, allFaarenVehicles);
        const facets: Facets = handleFacets(listServiceData?.facets || {}, translate, settings);
        const numFound: number = listServiceData?.searchResult?.numFound || 0;

        if (!checkUndefinedNullOrEmpty(searchForOcto)) {
            trackEvent(
                TrackEventSubType.EventSearch,
                {
                    event_origin: settings?.general?.isLandingpage ? TrackEventOrigin.LANDINGPAGE : TrackEventOrigin.HPT,
                    event_source_object: 'search',
                    search: searchForOcto,
                },
                settings?.general?.debug === DebugLevel.TRACKING || settings?.general?.debug >= DebugLevel.INFO
            );
        }
        return { cars, error: undefined, facets, numFound };
    } catch (error: any) {
        return { cars: {}, error, facets: {}, numFound: 0 };
    }
}
